import { partition } from './collection'

/**
 * Formats a file size in bytes to a human-readable string
 * @example formatFileSize(0) // '0 Bytes'
 * @example formatFileSize(1024) // '1 KB'
 * @example formatFileSize(1536) // '1.5 KB'
 * @example formatFileSize(1048576) // '1 MB'
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// Certificates are property attachments; a mittauspöytäkirja per protocol has
// reportId, `combined` marks the one PDF of all a visit's protocols, and
// sourceHash fingerprints what a mittauspöytäkirja PDF was made from.
export type CertificateMetadata =
  | {
      type?: string
      certificateType?: string
      visitId?: number
      reportId?: string
      combined?: boolean
      sourceHash?: string
    }
  | null
  | undefined

/**
 * Splits attachments into the ones to show and the per-protocol
 * mittauspöytäkirja PDFs that their visit's combined PDF already contains.
 */
export function partitionCombinedMeasurementParts<
  T extends { metadata?: unknown },
>(attachments: T[]): [shown: T[], parts: T[]] {
  const metadata = (attachment: T) => attachment.metadata as CertificateMetadata
  const combinedVisitIds = new Set(
    attachments
      .filter((attachment) => metadata(attachment)?.combined)
      .map((attachment) => metadata(attachment)?.visitId),
  )
  const [parts, shown] = partition(
    attachments,
    (attachment) =>
      !!metadata(attachment)?.reportId &&
      combinedVisitIds.has(metadata(attachment)?.visitId),
  )
  return [shown, parts]
}
